/* FondoCero · Core + cart: helpers (window.FC), queued Cart API, auto-added gifts, campaign calendar, <fc-countdown> and <fc-cart-drawer>. Safe to include more than once: it only runs once. */
(() => {
  'use strict';

  const FC = (window.FC = window.FC || {});
  if (FC.cart) return;

  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const route = (path) => shopRoot + String(path).replace(/^\//, '');
  // Money follows the shopper's market currency (USD in the US market → $69.99) and the storefront language.
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const lang = document.documentElement.lang || 'en';
  FC.locale = lang === 'en' ? 'en-US' : lang;
  const moneyFormat = new Intl.NumberFormat(FC.locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

  FC.money = (cents) => moneyFormat.format((Number(cents) || 0) / 100);

  // ---------- Campaign (Theme settings › FondoCero · Campaign; phase computed in <head> by snippets/fc-tokens.liquid)
  const campaign = window.FC_CAMPAIGN || { tz: 'America/New_York', salePct: 0, phase: () => 'after', now: () => Date.now() };
  FC.tz = campaign.tz || 'America/New_York';
  FC.zonedToUtc = window.FC_ZONED_TO_UTC || (() => null);
  FC.now = () => (campaign.now ? campaign.now() : Date.now());
  FC.phase = () => (campaign.phase ? campaign.phase() : 'after');
  FC.salePct = () => (FC.phase() === 'sale' ? Number(campaign.salePct) || 0 : 0);
  // Same rounding as Shopify's percentage discount: the discount amount is rounded to the cent.
  FC.discounted = (cents, pct = FC.salePct()) => cents - Math.round((cents * pct) / 100);
  // Pricing for one set option. baseUnit = regular price of a single set (savings are shown vs. buying single sets).
  FC.pricing = (pack, baseUnit) => {
    const pct = FC.salePct();
    const now = FC.discounted(pack.price, pct);
    const was = pct ? pack.price : pack.ref > pack.price ? pack.ref : 0;
    const singles = (baseUnit || 0) * pack.units;
    return {
      now,
      was,
      pct: pct || (was ? Math.round(((was - now) * 100) / was) : 0),
      save: Math.max(0, Math.max(singles, was) - now),
      unit: Math.round(now / pack.units),
    };
  };
  FC.applyCampaignName = () => {
    const name = FC.phase() === 'sale' ? campaign.saleName : campaign.holidayName;
    if (!name) return;
    document.querySelectorAll('[data-fc-campaign]').forEach((el) => {
      if (el.textContent !== name) el.textContent = name;
    });
  };
  // Switch phase live if the shopper has the page open across a boundary.
  const nextBoundary = () => [campaign.saleEndMs, campaign.cutoffMs].filter((t) => t && t > FC.now()).sort((a, b) => a - b)[0];
  const scheduleBoundary = () => {
    const next = nextBoundary();
    if (!next || next - FC.now() > 2 ** 31 - 1) return;
    setTimeout(() => {
      document.documentElement.setAttribute('data-fc-phase', FC.phase());
      FC.applyCampaignName();
      document.dispatchEvent(new CustomEvent('fc:phase', { detail: { phase: FC.phase() } }));
      scheduleBoundary();
    }, next - FC.now() + 50);
  };
  scheduleBoundary();

  // <fc-countdown> (snippets/fc-countdown.liquid): counts down to the sale end only. Box states: pending → live → expired.
  class FcCountdown extends HTMLElement {
    connectedCallback() {
      this.box = this.closest('[data-fc-countdown]');
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    setState(state) {
      if (this.box && this.box.dataset.state !== state) this.box.dataset.state = state;
    }
    tick() {
      const end = campaign.saleEndMs;
      if (!end || FC.now() >= end) {
        clearInterval(this.timer);
        this.setState('expired');
        return;
      }
      this.setState('live');
      const total = Math.max(0, Math.floor((end - FC.now()) / 1000));
      const days = Math.floor(total / 86400);
      const h = Math.floor((total % 86400) / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, '0');
      const units = days > 0 ? [[days, 'days'], [h, 'hrs'], [m, 'min']] : [[h, 'hrs'], [m, 'min'], [s, 'sec']];
      this.innerHTML = units
        .map(([v, u]) => `<span class="fc-timer__box"><span class="fc-timer__num">${pad(v)}</span><span class="fc-timer__unit">${u}</span></span>`)
        .join('<span class="fc-timer__sep" aria-hidden="true">:</span>');
      this.setAttribute('aria-label', days > 0 ? `${days} days and ${h} hours left` : `${h} hours and ${m} minutes left`);
    }
  }
  if (!customElements.get('fc-countdown')) customElements.define('fc-countdown', FcCountdown);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', FC.applyCampaignName);
  else FC.applyCampaignName();
  FC.esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  FC.reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  FC.fill = (template, values) => String(template || '').replace(/\{(\w+)\}/g, (m, key) => (key in values ? values[key] : m));

  FC.setConfig = (config) => {
    FC.config = config;
    document.dispatchEvent(new CustomEvent('fc:ready', { detail: config }));
  };
  FC.onReady = (fn) => {
    if (FC.config) fn(FC.config);
    else document.addEventListener('fc:ready', (event) => fn(event.detail), { once: true });
  };

  FC.animateMoney = (elements, toCents) => {
    elements.forEach((el) => {
      const from = Number(el.dataset.fcValue || toCents);
      el.dataset.fcValue = toCents;
      if (from === toCents || FC.reducedMotion()) {
        el.textContent = FC.money(toCents);
        return;
      }
      const start = performance.now();
      const duration = 320;
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = FC.money(Math.round(from + (toCents - from) * eased));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  };

  FC.reveal = () => {
    const elements = document.querySelectorAll('.fc-reveal:not(.is-in)');
    if (!('IntersectionObserver' in window) || FC.reducedMotion()) {
      elements.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px' }
    );
    elements.forEach((el) => observer.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', FC.reveal);
  else FC.reveal();
  document.addEventListener('shopify:section:load', FC.reveal);

  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task);
    queue = run.catch(() => {});
    return run;
  };

  async function api(path, body) {
    const options = body
      ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }
      : { headers: { Accept: 'application/json' }, cache: 'no-store' };
    const response = await fetch(route(path), options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'We couldn\x27t update your cart. Please try again.');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  const getCart = () => api('cart.js');

  const isGiftLine = (item) => Boolean(item.properties && item.properties._fc_gift);
  const packByVariant = (variantId) => ((FC.config && FC.config.packs) || []).find((p) => p.variantId === variantId);
  FC.isGiftLine = isGiftLine;
  FC.packByVariant = packByVariant;

  function unitsInCart(cart) {
    const config = FC.config;
    if (!config) return 0;
    return cart.items.reduce((total, item) => {
      if (item.product_id !== config.productId || isGiftLine(item)) return total;
      const pack = packByVariant(item.variant_id);
      return total + item.quantity * (pack ? pack.units : 1);
    }, 0);
  }

  async function syncGifts(cart) {
    const config = FC.config;
    if (!config || !config.gifts || !config.gifts.length) return cart;
    const units = unitsInCart(cart);
    const toAdd = [];
    const updates = {};
    config.gifts.forEach((gift) => {
      const lines = cart.items.filter((item) => item.variant_id === gift.variantId && isGiftLine(item));
      const wanted = units >= gift.minUnits;
      if (wanted && !lines.length) toAdd.push({ id: gift.variantId, quantity: 1, properties: { _fc_gift: '1' } });
      lines.forEach((line, i) => {
        const quantity = wanted && i === 0 ? 1 : 0;
        if (line.quantity !== quantity) updates[line.key] = quantity;
      });
    });
    const hasUpdates = Object.keys(updates).length > 0;
    if (hasUpdates) await api('cart/update.js', { updates });
    if (toAdd.length) {
      await Promise.all(toAdd.map((item) => api('cart/add.js', { items: [item] }).catch((err) => console.warn('[FC] Gift not added:', err.message))));
    }
    return hasUpdates || toAdd.length ? getCart() : cart;
  }

  function publish(cart, action) {
    FC.lastCart = cart;
    document.dispatchEvent(new CustomEvent('fc:cart', { detail: { cart, action } }));
    document.querySelectorAll('cart-icon').forEach((icon) => {
      try {
        if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, action === 'add');
      } catch (e) {}
    });
  }

  FC.cart = {
    get: getCart,
    sync: syncGifts,
    add(items) {
      return enqueue(async () => {
        await api('cart/add.js', { items });
        const cart = await syncGifts(await getCart());
        publish(cart, 'add');
        return cart;
      });
    },
    change(key, quantity) {
      return enqueue(async () => {
        const cart = await syncGifts(await api('cart/change.js', { id: key, quantity }));
        publish(cart, 'change');
        return cart;
      });
    },
    swap(key, currentQuantity, variantId) {
      return enqueue(async () => {
        await api('cart/change.js', { id: key, quantity: Math.max(0, currentQuantity - 1) });
        await api('cart/add.js', { items: [{ id: variantId, quantity: 1 }] });
        const cart = await syncGifts(await getCart());
        publish(cart, 'change');
        return cart;
      });
    },
    refresh() {
      return enqueue(async () => {
        const cart = await syncGifts(await getCart());
        publish(cart, 'refresh');
        return cart;
      });
    },
    open(cart) {
      if (FC.drawer) FC.drawer.open(cart);
      else window.location.href = route('cart');
    },
  };

  class FcCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-fc-panel]');
      this.body = this.querySelector('[data-fc-lines]');
      this.upgradeEl = this.querySelector('[data-fc-upgrade]');
      this.shipEl = this.querySelector('[data-fc-ship]');
      this.footEl = this.querySelector('[data-fc-foot]');
      this.errorEl = this.querySelector('[data-fc-drawer-error]');
      this.t = JSON.parse(this.querySelector('[data-fc-drawer-strings]').textContent);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('fc:cart', this.onCart);
      if (this.dataset.intercept === 'true') window.addEventListener('click', this.onHeaderClick, true);
      FC.drawer = this;
    }

    disconnectedCallback() {
      document.removeEventListener('fc:cart', this.onCart);
      window.removeEventListener('click', this.onHeaderClick, true);
      if (FC.drawer === this) FC.drawer = null;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(cart) {
      if (!this.isOpen) {
        this.lastFocus = document.activeElement;
        this.classList.add('is-open');
        this.removeAttribute('inert');
        this.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('fc-lock');
        requestAnimationFrame(() => this.querySelector('[data-fc-close]').focus({ preventScroll: true }));
      }
      if (cart) {
        this.render(cart);
      } else {
        if (FC.lastCart) this.render(FC.lastCart);
        this.setBusy(true);
        FC.cart.refresh().catch((err) => this.showError(err.message)).finally(() => this.setBusy(false));
      }
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fc-lock');
      if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.panel.setAttribute('aria-busy', String(busy));
    }

    showError(message) {
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }

    onCart = (event) => {
      if (this.isOpen || event.detail.action !== 'add') this.render(event.detail.cart);
    };

    onHeaderClick = (event) => {
      const trigger = event.target.closest('[aria-controls="cart-drawer"], [data-testid="cart-drawer-trigger"], a[href$="/cart"]');
      if (!trigger || this.contains(trigger) || !trigger.closest('#header-group, header')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    };

    onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe')].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    onClick = (event) => {
      if (event.target.closest('[data-fc-close]')) {
        this.close();
        return;
      }
      const qty = event.target.closest('[data-fc-qty]');
      const remove = event.target.closest('[data-fc-remove]');
      const upgrade = event.target.closest('[data-fc-upgrade-btn]');
      const addUpsell = event.target.closest('[data-fc-add-upsell]');
      let task = null;
      if (qty) task = () => FC.cart.change(qty.dataset.key, Math.max(0, Number(qty.dataset.qty)));
      else if (remove) task = () => FC.cart.change(remove.dataset.key, 0);
      else if (upgrade) task = () => FC.cart.swap(upgrade.dataset.key, Number(upgrade.dataset.qty), Number(upgrade.dataset.variant));
      else if (addUpsell) task = () => FC.cart.add([{ id: Number(addUpsell.dataset.fcAddUpsell), quantity: 1 }]);
      if (!task) return;
      this.showError('');
      this.setBusy(true);
      task().catch((err) => this.showError(err.message)).finally(() => this.setBusy(false));
    };

    render(cart) {
      if (!cart) return;
      const t = this.t;
      const count = cart.item_count;
      this.querySelector('[data-fc-count]').textContent = count ? `(${count})` : '';
      if (!cart.items.length) {
        this.body.innerHTML = `<div class="fc-drawer__empty"><p>${FC.esc(t.empty)}</p><button type="button" class="fc-btn" data-fc-close>${FC.esc(t.continue)}</button></div>`;
        this.upgradeEl.innerHTML = '';
        this.shipEl.hidden = true;
        this.footEl.hidden = true;
        return;
      }
      const items = [...cart.items].sort((a, b) => Number(isGiftLine(a)) - Number(isGiftLine(b)));
      this.body.innerHTML = items.map((item) => this.lineTemplate(item)).join('');
      this.renderShipping(cart);
      this.upgradeEl.innerHTML = this.upgradeTemplate(cart);
      this.footEl.hidden = false;
      this.querySelector('[data-fc-subtotal]').textContent = FC.money(cart.total_price);
      const discountRow = this.querySelector('[data-fc-discount-row]');
      const discount = cart.items.reduce((sum, item) => sum + (item.original_line_price - item.final_line_price), 0);
      discountRow.hidden = discount <= 0;
      this.querySelector('[data-fc-discount]').textContent = `−${FC.money(discount)}`;
    }

    renderShipping(cart) {
      const rate = Number((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
      const threshold = Math.round(Number(this.dataset.threshold || 0) * rate);
      if (!threshold) {
        this.shipEl.hidden = true;
        return;
      }
      const remaining = threshold - cart.total_price;
      const pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      this.shipEl.hidden = false;
      this.shipEl.classList.toggle('is-done', remaining <= 0);
      this.shipEl.querySelector('[data-fc-ship-text]').innerHTML =
        remaining > 0 ? FC.fill(FC.esc(this.t.shipping_left), { amount: `<strong>${FC.money(remaining)}</strong>` }) : FC.esc(this.t.shipping_done);
      this.shipEl.querySelector('[data-fc-ship-fill]').style.setProperty('--fc-p', `${pct}%`);
    }

    lineTemplate(item) {
      const t = this.t;
      const gift = isGiftLine(item);
      const config = FC.config;
      const pack = config && item.product_id === config.productId ? packByVariant(item.variant_id) : null;
      const title = pack ? `${item.product_title} · ${pack.short || pack.title}` : item.product_title;
      let image = '';
      if (item.image) {
        try {
          const url = new URL(item.image, window.location.href);
          url.searchParams.set('width', '160');
          image = `<img class="fc-line__img" src="${FC.esc(url.toString())}" alt="" width="70" height="70" loading="lazy">`;
        } catch (e) {
          image = '';
        }
      }
      const subs = [];
      if (!pack && item.variant_title && !item.product_has_only_default_variant) subs.push(FC.esc(item.variant_title));
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (value && key.charAt(0) !== '_') subs.push(`${FC.esc(key)}: ${FC.esc(value)}`);
      });
      const isFree = item.final_line_price === 0;
      const price = isFree ? `<span class="fc-line__free">${FC.esc(t.free)}</span>` : FC.money(item.final_line_price);
      const original = item.original_line_price > item.final_line_price ? `<s>${FC.money(item.original_line_price)}</s>` : '';
      const actions = gift
        ? ''
        : `<div class="fc-line__actions"><div class="fc-qty"><button type="button" data-fc-qty data-key="${FC.esc(item.key)}" data-qty="${item.quantity - 1}" aria-label="${FC.esc(t.decrease)}">${ICONS.minus}</button><span class="fc-qty__val">${item.quantity}</span><button type="button" data-fc-qty data-key="${FC.esc(item.key)}" data-qty="${item.quantity + 1}" aria-label="${FC.esc(t.increase)}">${ICONS.plus}</button></div><button type="button" class="fc-line__remove" data-fc-remove data-key="${FC.esc(item.key)}">${FC.esc(t.remove)}</button></div>`;
      return `<div class="fc-line">${image || '<span class="fc-line__img"></span>'}<div class="fc-line__info">${gift ? `<span class="fc-line__tag">${ICONS.gift}${FC.esc(t.gift)}</span>` : ''}<span class="fc-line__title">${FC.esc(title)}</span>${subs.length ? `<span class="fc-line__sub">${subs.join(' · ')}</span>` : ''}${actions}</div><div class="fc-line__price">${price}${original}</div></div>`;
    }

    upgradeTemplate(cart) {
      const config = FC.config;
      if (!config || this.dataset.upgrade !== 'true') return '';
      const lines = cart.items
        .filter((item) => item.product_id === config.productId && !isGiftLine(item))
        .map((item) => ({ item, pack: packByVariant(item.variant_id) }))
        .filter((entry) => entry.pack)
        .sort((a, b) => b.pack.units - a.pack.units);
      if (!lines.length) return '';
      const current = lines[0];
      const next = config.packs.filter((p) => p.available && p.units > current.pack.units).sort((a, b) => a.units - b.units)[0];
      if (!next) return this.upsellTemplate(cart);
      // Sale-aware: compare discounted prices while the sale is on (the automatic discount applies at checkout).
      const nextPrice = FC.pricing(next, config.baseUnit);
      const extra = nextPrice.now - FC.pricing(current.pack, config.baseUnit).now;
      const name = FC.esc(next.short || next.title);
      const text =
        nextPrice.save > 0
          ? FC.fill(FC.esc(this.t.upgrade_save), { pack: `<strong>${name}</strong>`, savings: `<strong>${FC.money(nextPrice.save)}</strong>` })
          : FC.fill(FC.esc(this.t.upgrade_plain), { pack: `<strong>${name}</strong>` });
      return `<span class="fc-upgrade__icon">${ICONS.gift}</span><p class="fc-upgrade__text">${text}</p><button type="button" class="fc-btn fc-upgrade__btn" data-fc-upgrade-btn data-key="${FC.esc(current.item.key)}" data-qty="${current.item.quantity}" data-variant="${next.variantId}">${FC.esc(this.t.upgrade_button)} · +${FC.money(extra)}</button>`;
    }

    upsellTemplate(cart) {
      const config = FC.config;
      if (!config || !config.upsells || !config.upsells.length) return '';
      const inCart = new Set(cart.items.map((item) => item.variant_id));
      const items = config.upsells.filter((u) => !inCart.has(u.variantId)).slice(0, 2);
      if (!items.length) return '';
      return `<div class="fc-xsell"><p class="fc-xsell__title">${FC.esc(this.t.xsell_title)}</p>${items
        .map(
          (u) => `<div class="fc-xsell__item">${u.image ? `<img class="fc-xsell__img" src="${FC.esc(u.image)}" alt="" width="46" height="46" loading="lazy">` : '<span class="fc-xsell__img"></span>'}<span class="fc-xsell__name">${FC.esc(u.title)}<span class="fc-xsell__price">${FC.money(u.price)}${u.compareAt > u.price ? ` <s>${FC.money(u.compareAt)}</s>` : ''}</span></span><button type="button" class="fc-xsell__add" data-fc-add-upsell="${u.variantId}" aria-label="${FC.esc(this.t.xsell_add)}: ${FC.esc(u.title)}">${ICONS.plus}${FC.esc(this.t.xsell_add)}</button></div>`
        )
        .join('')}</div>`;
    }
  }

  const svg = (d, size = 16) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
  const ICONS = {
    minus: svg('<path d="M5 12h14"/>', 14),
    plus: svg('<path d="M12 5v14M5 12h14"/>', 14),
    gift: svg('<rect x="3.5" y="8.5" width="17" height="4" rx="1"/><path d="M5 12.5v8h14v-8M12 8.5v12"/><path d="M12 8.5S10.8 4 8.3 4a2.2 2.2 0 000 4.5H12zM12 8.5S13.2 4 15.7 4a2.2 2.2 0 010 4.5H12z"/>', 14),
  };

  if (!customElements.get('fc-cart-drawer')) customElements.define('fc-cart-drawer', FcCartDrawer);
})();
